/** Workspace-aware branding for PDF generation popup tabs. */

import { getAdminUser, getTenantLogo } from "../api/adminClient";
import { buildWorkspaceIdentity } from "../ai/workspaceContext";
import { DEFAULT_BRAND_COLOR } from "./participantBranding";

const CACHE_KEY = "5x_workspace_pdf_branding";
const CLASSIC_LOGO = "/5x-logo-tagline.png";
const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function normalizeHex(color, fallback = DEFAULT_BRAND_COLOR) {
  const raw = String(color || "").trim();
  return HEX.test(raw) ? raw : fallback;
}

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Persist workspace chrome for synchronous PDF splash (admin). */
export function cacheWorkspacePdfBranding(patch) {
  if (!patch || typeof patch !== "object") return;
  const tenantId = String(patch.tenant_id || "").trim();
  if (!tenantId) return;
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        tenant_id: tenantId,
        product_display_name: String(patch.product_display_name || "").trim(),
        logo_url: String(patch.logo_url || patch.tenant_logo || "").trim(),
        primary_color: normalizeHex(patch.primary_color),
        email_pack: patch.email_pack || "generic",
        is_classic: false,
      })
    );
  } catch {
    /* quota / private mode */
  }
}

export function clearWorkspacePdfBrandingCache() {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

function cachedForTenant(tenantId) {
  const cache = readCache();
  if (!cache || !tenantId) return null;
  return String(cache.tenant_id || "") === String(tenantId) ? cache : null;
}

/**
 * Resolve branding for admin PDF splash (sync — call on user click).
 * Classic workspace → 5X NEXUS orb; tenant → logo + product name + primary color.
 */
export function getAdminPdfBranding(overrides = null) {
  if (overrides && typeof overrides === "object") {
    return normalizePdfSplashBranding(overrides);
  }

  const user = getAdminUser();
  const identity = buildWorkspaceIdentity(user);
  const isClassic = Boolean(identity.is_classic || !user?.tenant_id || user?.is_platform_admin);

  if (isClassic) {
    return normalizePdfSplashBranding({
      is_classic: true,
      product_display_name: identity.product_display_name || "5x Team Assessment",
      brand_label: "5X NEXUS",
      logo_url: CLASSIC_LOGO,
      primary_color: DEFAULT_BRAND_COLOR,
      email_pack: "5x",
    });
  }

  const tenantId = String(user?.tenant_id || "");
  const cache = cachedForTenant(tenantId);
  const product =
    cache?.product_display_name ||
    user?.product_display_name?.trim() ||
    identity.product_display_name ||
    user?.tenant_name?.trim() ||
    identity.tenant_name ||
    "Team Assessment";
  const logo =
    cache?.logo_url || getTenantLogo() || user?.tenant_logo || "";
  const color = normalizeHex(cache?.primary_color || user?.primary_color);

  return normalizePdfSplashBranding({
    is_classic: false,
    tenant_id: tenantId,
    product_display_name: product,
    brand_label: product,
    logo_url: logo,
    primary_color: color,
    email_pack: cache?.email_pack || user?.email_pack || identity.email_pack || "generic",
  });
}

/** Participant assessment branding → PDF splash. */
export function getParticipantPdfBranding(branding) {
  const src = branding && typeof branding === "object" ? branding : {};
  const usesFiveX =
    src.email_pack !== "generic" &&
    !src.tenant_id &&
    src.uses_five_x_branding !== false;

  if (usesFiveX) {
    return normalizePdfSplashBranding({
      is_classic: true,
      product_display_name:
        src.product_display_name?.trim() || "5x Team Assessment",
      brand_label: "5X NEXUS",
      logo_url: CLASSIC_LOGO,
      primary_color: normalizeHex(src.primary_color),
      email_pack: "5x",
    });
  }

  const product =
    src.product_display_name?.trim() ||
    src.tenant_name?.trim() ||
    src.company_name?.trim() ||
    "Team Assessment";

  return normalizePdfSplashBranding({
    is_classic: false,
    product_display_name: product,
    brand_label: product,
    logo_url: src.logo_url || "",
    primary_color: normalizeHex(src.primary_color),
    email_pack: src.email_pack || "generic",
  });
}

/** Dashboard hero orb — ARIA label + extended screen-reader description. */
export function getDashboardOrbAccessibility() {
  const branding = getAdminPdfBranding();
  if (branding.is_classic) {
    return {
      ariaLabel: "5X NEXUS — Your AI Partner",
      screenReaderText:
        "5X NEXUS is your AI partner for leadership assessments, portfolio insights, and workflow automation.",
    };
  }
  const product =
    branding.product_display_name || branding.brand_label || "Team Assessment";
  return {
    ariaLabel: `${product} NEXUS — Your AI assistant`,
    screenReaderText: `NEXUS AI assistant for ${product}. Ask questions, review insights, and run assessment workflows.`,
  };
}

/** CSS custom properties for tenant primary color on dashboard / nav chrome. */
export function getWorkspaceBrandStyle(branding) {
  const color = branding?.primary_color;
  if (!color || branding?.is_classic) return undefined;
  return {
    "--dash-brand": color,
    "--dash-brand-light": color,
    "--nexus-brand": color,
    "--nexus-brand-light": color,
  };
}

/** Compact nav-bar orb (sub-pages) — classic 5X vs tenant workspace. */
export function getNavOrbPresentation() {
  const branding = getAdminPdfBranding();
  const a11y = getDashboardOrbAccessibility();

  if (branding.is_classic) {
    return {
      ...branding,
      ...a11y,
      variant: "classic",
      prefix: "5X",
      brand: "NEXUS",
      showLogo: false,
      title: "Go to NEXUS command center",
    };
  }

  const workspaceName =
    branding.product_display_name || branding.brand_label || "Team Assessment";
  const initials = workspaceName
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();

  return {
    ...branding,
    ...a11y,
    variant: "workspace",
    prefix: branding.logo_url ? "" : initials,
    brand: "NEXUS",
    showLogo: Boolean(branding.logo_url),
    title: `Go to ${workspaceName} NEXUS`,
  };
}

/** Visible dashboard orb copy + branding (classic vs tenant workspace). */
export function getDashboardOrbPresentation() {
  const branding = getAdminPdfBranding();
  const a11y = getDashboardOrbAccessibility();

  if (branding.is_classic) {
    return {
      ...branding,
      ...a11y,
      variant: "classic",
      prefix: "5X",
      brand: "NEXUS",
      tagline: "Your AI Partner",
      showLogo: false,
    };
  }

  const workspaceName =
    branding.product_display_name || branding.brand_label || "Team Assessment";

  return {
    ...branding,
    ...a11y,
    variant: "workspace",
    prefix: workspaceName,
    brand: "NEXUS",
    tagline: "Your AI assistant",
    showLogo: Boolean(branding.logo_url),
  };
}

/** Whether the signed-in admin is on the classic 5X workspace (not a tenant). */
export function isClassicAdminWorkspace(user = getAdminUser()) {
  const identity = buildWorkspaceIdentity(user);
  return Boolean(identity.is_classic || !user?.tenant_id || user?.is_platform_admin);
}

/** Product / workspace label for admin chrome (top nav, etc.). */
export function getAdminWorkspaceDisplayName() {
  const branding = getAdminPdfBranding();
  if (branding.is_classic) {
    return branding.product_display_name || "5x Team Assessment";
  }
  return branding.product_display_name || branding.brand_label || "Team Assessment";
}

export function normalizePdfSplashBranding(raw) {
  const isClassic = Boolean(raw?.is_classic);
  const product = String(
    raw?.product_display_name || raw?.brand_label || ""
  ).trim();
  const brandLabel = String(
    raw?.brand_label || (isClassic ? "5X NEXUS" : product) || "Team Assessment"
  ).trim();
  const logo = String(raw?.logo_url || "").trim();
  const color = normalizeHex(raw?.primary_color);

  return {
    is_classic: isClassic,
    product_display_name: product || brandLabel,
    brand_label: brandLabel,
    logo_url: logo,
    primary_color: color,
    email_pack: raw?.email_pack || (isClassic ? "5x" : "generic"),
  };
}
